import React, { useEffect, useState } from "react";
import { Gift, Copy, Check, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { copyToClipboard, shareNative } from "@/lib/share";

export function ReferralCard({ compact = false }) {
  const [ref, setRef] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/referral").then((r) => setRef(r.data)).catch((e) => console.warn("Referral card unavailable", e?.response?.status)); }, []);
  if (!ref) return null;

  const link = `${window.location.origin}/?ref=${ref.code}`;
  const text = `I use Powerball Oracle — 7 statistical engines + AI oracles for my Powerball picks. Try it with my link:`;
  const copy = async () => {
    if (!(await copyToClipboard(link, "Referral link copied"))) return;
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const enc = encodeURIComponent;
  const links = [
    { name: "X", href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(link)}` },
    { name: "WhatsApp", href: `https://api.whatsapp.com/send?text=${enc(text + " " + link)}` },
    { name: "Telegram", href: `https://t.me/share/url?url=${enc(link)}&text=${enc(text)}` },
    { name: "Email", href: `mailto:?subject=${enc("Try Powerball Oracle")}&body=${enc(text + "\n\n" + link)}` },
  ];

  return (
    <div data-testid="referral-card" className={`bg-surface border border-oracle/20 rounded-xl ${compact ? "p-4" : "p-5 sm:p-6 mb-6"}`}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-oracle/10 border border-oracle/30 flex items-center justify-center shrink-0"><Gift className="w-4 h-4 text-oracle" strokeWidth={1.5} /></div>
          <div>
            {ref.reward_type === "credit" ? (
              <>
                <div className="font-heading text-xl text-parchment">Invite a friend, earn <span className="italic text-oracle">${ref.credit_usd.toFixed(2)} off your next bill</span></div>
                <p className="font-body text-xs text-parchment/55 mt-1 max-w-md">Every friend who starts a paid subscription through your link credits ${ref.credit_usd.toFixed(2)} to your Stripe account — applied automatically to your next invoice.</p>
              </>
            ) : (
              <>
                <div className="font-heading text-xl text-parchment">Invite a friend, earn <span className="italic text-oracle">{ref.bonus_days} free days</span></div>
                <p className="font-body text-xs text-parchment/55 mt-1 max-w-md">Every friend who starts a paid subscription through your link adds {ref.bonus_days} days of access for you. Subscribers earn ${ref.credit_usd.toFixed(2)} bill credit instead.</p>
              </>
            )}
            <div className="font-body text-[11px] text-parchment/50 mt-2 flex flex-wrap gap-x-4 gap-y-1" data-testid="referral-stats">
              <span><span className="text-oracle">{ref.clicks}</span> visits</span>
              <span><span className="text-oracle">{ref.referred_purchases}</span> friends joined</span>
              {ref.credits_earned_usd > 0 && <span><span className="text-oracle">${ref.credits_earned_usd.toFixed(2)}</span> credit earned</span>}
              {(ref.days_earned > 0 || !ref.credits_earned_usd) && <span><span className="text-oracle">{ref.days_earned}</span> days earned</span>}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 min-w-0 lg:min-w-[280px]">
          <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-md px-3 py-2">
            <code data-testid="referral-link" className="font-body text-xs text-parchment/80 truncate flex-1">{link}</code>
            <button data-testid="referral-copy-btn" onClick={copy} className="text-parchment/60 hover:text-oracle transition-colors">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button>
          </div>
          <div className="flex gap-1.5">
            {links.map((l) => (
              <a key={l.name} href={l.href} target="_blank" rel="noreferrer" data-testid={`referral-share-${l.name.toLowerCase()}`} className="flex-1 label-caps text-center text-parchment/80 hover:text-oracle border border-white/10 hover:border-oracle/40 rounded-md py-2 transition-colors">{l.name}</a>
            ))}
            {navigator.share && (
              <Button data-testid="referral-native-share" variant="ghost" size="sm" onClick={() => shareNative({ title: "Powerball Oracle", text, url: link })} className="text-parchment hover:text-oracle hover:bg-white/5"><Share2 className="w-4 h-4" /></Button>
            )}
          </div>
          <div className="font-body text-[10px] text-parchment/35">Your code: <span data-testid="referral-code" className="text-parchment/70 tracking-widest">{ref.code}</span></div>
        </div>
      </div>
    </div>
  );
}
